import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, throwIfNotOk } from '../lib/api';
import { qk } from '../lib/queryKeys';
import { useAuth } from '../lib/authContext';
import { ProfileLockedCard } from '../components/ProfileLockedCard';
import { formatDate, formatUsd } from '../lib/format';
import { formatDeliveryWindow } from '../lib/dates';
import type { AssetMatch } from '@siliconx/shared/dto/direct-offer';
import { QueryBoundary } from '@siliconx/shared/ui/QueryBoundary';
import { conditionLabel } from '@siliconx/shared/ui/ConditionBadge';
import { Card } from '@siliconx/shared/ui/Card';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { Button } from '@siliconx/shared/ui/Button';
import { requireOrgFeature } from '../lib/orgFeatureGate';

/**
 * SIL-646 — "which of my idle assets match open buyer demand, at what
 * price." Backed by GET /api/client/asset-matches (asset-matches.ts): a
 * plain scored SQL join, no ML, no opt-in gate (distinct from /opportunities,
 * SIL-170's accept_direct_offer()-actioned "Buyer Demand" feed). Each row's
 * "List at $X" goes straight through the ordinary POST /api/client/listings
 * asset-listing path, priced at the matched demand's pricePerUnitUsd.
 */
// SIL-1108: `orgFeature: 'marketplaceOps'` (customerNav.ts) is
// business-only — gated here so a direct hit doesn't fire this page's
// mount-time fetch before the root `OrgFeatureGate`'s (__root.tsx) redirect
// lands.
export const Route = createFileRoute('/matches')({
  beforeLoad: () => requireOrgFeature('marketplaceOps'),
  component: MatchesPage,
});

interface MatchesResponse {
  data: AssetMatch[];
}

function scoreLabel(scorePct: number | null): string {
  if (scorePct == null) return '—';
  const sign = scorePct > 0 ? '+' : '';
  return `${sign}${scorePct.toFixed(1)}%`;
}

function scoreTone(scorePct: number | null): string {
  if (scorePct == null) return 'text-muted-foreground';
  if (scorePct > 0) return 'text-green-700';
  if (scorePct < 0) return 'text-amber-700';
  return 'text-muted-foreground';
}

function MatchRow({ match }: { match: AssetMatch }) {
  const queryClient = useQueryClient();

  const listMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.listings.$post({
        json: { assetId: match.assetId, askingPriceUsd: match.pricePerUnitUsd },
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success('Listing created as a draft, priced at the matched demand. Activate it to publish to the exchange.');
      void queryClient.invalidateQueries({ queryKey: qk.assetMatches() });
      void queryClient.invalidateQueries({ queryKey: qk.listings.all });
    },
    onError: (err: Error & { body?: { error?: { code?: string; message?: string } } }) => {
      toast.error(err.body?.error?.message ?? err.message ?? 'Could not create listing.');
    },
  });

  return (
    <tr className="hover:bg-muted">
      <td className="px-4 py-3">
        <div className="font-mono text-xs text-foreground">
          {match.assetSerialNumber ?? match.assetId.slice(0, 8)}
        </div>
        <div className="text-xs text-muted-foreground">
          {[match.manufacturerName, match.skuModelName].filter(Boolean).join(' ') || match.skuId.slice(0, 8)}
        </div>
      </td>
      <td className="px-4 py-3 text-foreground">{conditionLabel(match.conditionBand)}</td>
      <td className="px-4 py-3 text-muted-foreground">{match.regionId ?? 'Any'}</td>
      <td className="px-4 py-3 text-right font-medium text-foreground">
        {formatUsd(match.pricePerUnitUsd)}
      </td>
      <td className="px-4 py-3 text-right text-muted-foreground">
        {match.fmvMedUsd != null ? formatUsd(match.fmvMedUsd) : '—'}
      </td>
      <td className={`px-4 py-3 text-right font-medium ${scoreTone(match.scorePct)}`}>
        {scoreLabel(match.scorePct)}
      </td>
      <td className="px-4 py-3 text-right text-muted-foreground">
        {match.quantityFilled}/{match.quantityDesired}
      </td>
      <td className="px-4 py-3 text-muted-foreground">
        {formatDeliveryWindow(match.deliverFrom, match.deliverBy)}
      </td>
      <td className="px-4 py-3 text-muted-foreground">{formatDate(match.createdAt)}</td>
      <td className="px-4 py-3 text-right">
        <div className="flex justify-end gap-3">
          <Link
            to="/assets/$assetId"
            params={{ assetId: match.assetId }}
            className="text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            View asset
          </Link>
          <Button size="sm" onClick={() => listMutation.mutate()} disabled={listMutation.isPending}>
            {listMutation.isPending ? 'Listing…' : `List at ${formatUsd(match.pricePerUnitUsd)}`}
          </Button>
        </div>
      </td>
    </tr>
  );
}

function MatchesPage() {
  const { canSell, sellerMissingFields } = useAuth();

  const matchesQuery = useQuery({
    queryKey: qk.assetMatches(),
    queryFn: async () => {
      const res = await api.api.client['asset-matches'].$get({ query: {} });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as MatchesResponse;
    },
    enabled: canSell,
  });

  if (!canSell) {
    return (
      <div className="space-y-6">
        <PageHeader title="Asset Matches" pinTo="/matches" />
        <ProfileLockedCard side="seller" missingFields={sellerMissingFields} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Asset Matches"
        subtitle="Idle inventory matched to open buyer demand, ranked by price versus current FMV."
        pinTo="/matches"
      />

      <QueryBoundary
        query={matchesQuery}
        errorTitle="Couldn't load asset matches."
        empty={() => false}
      >
        {(data) =>
          data.data.length === 0 ? (
            <Card className="text-center text-sm text-muted-foreground">
              No open buyer demand currently matches your unlisted inventory.
            </Card>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border bg-card">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted text-left text-xs font-medium text-muted-foreground">
                    <th className="px-4 py-3">Asset</th>
                    <th className="px-4 py-3">Condition</th>
                    <th className="px-4 py-3">Region</th>
                    <th className="px-4 py-3 text-right">Matched price</th>
                    <th className="px-4 py-3 text-right">Current FMV</th>
                    <th className="px-4 py-3 text-right">vs. FMV</th>
                    <th className="px-4 py-3 text-right">Qty filled/desired</th>
                    <th className="px-4 py-3">Delivery window</th>
                    <th className="px-4 py-3">Posted</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.data.map((m) => (
                    <MatchRow key={`${m.assetId}:${m.directOfferId}`} match={m} />
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </QueryBoundary>
    </div>
  );
}
